import { useState, useEffect, useCallback } from 'react';
import { 
  getMyPosts, 
  getMyReplies, 
  getMyRepliedPosts,
  searchMyPosts,
  searchMyReplies,
  searchMyRepliedPosts,
  MyPostsResponse,
  MyRepliesResponse,
  MyRepliedPostsResponse,
  MyPostSearchResponse,
  MyRepliesSearchResponse,
  MyRepliedPostsSearchResponse
} from '../services/mypageApi';

export type ActivityTab = 'posts' | 'comments' | 'commented';

interface UseMyActivityParams {
  activeTab: ActivityTab;
  searchQuery?: string;
}

// 게시글 아이템 통합 타입
export interface PostItem {
  id: number;
  title: string;
  category: string;
  lectureTitle?: string;
  chapter?: string | number;
  date: string;
  comments: number;
  views: number;
  pinned?: boolean;
  writerName?: string;
  htmlContent?: string;
  studyCategoryName?: string;
}

// 댓글 아이템 타입
export interface CommentItem {
  id: number;
  content: string;
  postTitle: string;
  postId?: number; // 게시글 ID 추가
  date: string;
  writerName?: string;
  images?: string[];
}

interface UseMyActivityReturn {
  data: (PostItem | CommentItem)[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
}

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  
  return `${year}년 ${month}월 ${day}일 ${hours}:${minutes}`;
};

// 게시글 데이터 변환
const transformPostData = (posts: any[]): PostItem[] => {
  return posts.map((post: any) => {
    // 검색 결과인 경우 (htmlcontent 필드가 있음)
    if ('htmlcontent' in post) {
      return {
        id: post.id,
        title: post.title,
        category: post.category,
        lectureTitle: post.lectureTitle,
        date: formatDate(post.postDate),
        comments: 0, // 검색 결과에는 댓글 수 정보가 없음
        views: 0, // 검색 결과에는 조회수 정보가 없음
        pinned: post.pinned,
        writerName: post.writerName,
        htmlContent: post.htmlcontent,
        studyCategoryName: post.studyCategoryName
      };
    }
    // 일반 목록인 경우 (post_id 필드가 있음)
    else if ('post_id' in post) {
      return {
        id: post.post_id,
        title: post.title,
        category: post.post_category,
        lectureTitle: post.lecture_name,
        chapter: post.chapter?.toString(),
        date: formatDate(post.created_at),
        comments: post.reply_cnt,
        views: post.view_cnt,
        pinned: post.pinned,
        writerName: post.writer_name,
        studyCategoryName: post.study_name
      };
    }
    // 기타 경우 (id 필드가 있는 검색 결과)
    else {
      return {
        id: post.id,
        title: post.title,
        category: post.category,
        lectureTitle: post.lectureTitle,
        date: formatDate(post.postDate),
        comments: 0,
        views: 0,
        pinned: post.pinned,
        writerName: post.writerName,
        studyCategoryName: post.studyCategoryName
      };
    }
  });
};

// 댓글 데이터 변환
const transformCommentData = (comments: any[]): CommentItem[] => {
  return comments.map((comment: any) => {
    // 검색 결과인 경우 (writer_name 필드가 있음)
    if ('writer_name' in comment) {
      return {
        id: comment.reply_id,
        content: comment.content,
        postTitle: '', // 검색 결과에는 게시글 제목이 없음
        date: formatDate(comment.reply_created_at),
        writerName: comment.writer_name,
        images: comment.images
        // postId는 검색 결과에 없음
      };
    }
    // 일반 목록인 경우 (reply_content 필드가 있음)
    else {
      return {
        id: comment.reply_id,
        content: comment.reply_content,
        postTitle: comment.post_title,
        postId: comment.post_id, // 게시글 ID 추가
        date: formatDate(comment.reply_created_at)
      };
    }
  });
};

export const useMyActivity = ({ activeTab, searchQuery }: UseMyActivityParams): UseMyActivityReturn => {
  const [data, setData] = useState<(PostItem | CommentItem)[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // 검색 모드인지 확인
  const isSearchMode = searchQuery && searchQuery.trim().length > 0;

  // 데이터 로드 함수
  const loadData = useCallback(async (pageNum: number, isRefresh = false) => {
    if (loading) return;
    
    setLoading(true);
    setError(null);

    try {
      const searchMode = !!(searchQuery && searchQuery.trim().length > 0);

      if (searchMode && searchQuery) {
        // 검색 모드
        let searchResults: (PostItem | CommentItem)[] = [];
        
        switch (activeTab) {
          case 'posts':
            const postSearchResults = await searchMyPosts(searchQuery);
            searchResults = transformPostData(postSearchResults);
            break;
          case 'comments':
            const commentSearchResults = await searchMyReplies(searchQuery);
            searchResults = transformCommentData(commentSearchResults);
            break;
          case 'commented':
            const repliedPostSearchResults = await searchMyRepliedPosts(searchQuery);
            searchResults = transformPostData(repliedPostSearchResults);
            break;
        }
        
        setData(searchResults);
        setHasMore(false); // 검색 결과는 한 번에 모든 결과를 반환
      } else {
        // 일반 목록 모드
        const size = 10;
        let result: any;
        
        switch (activeTab) {
          case 'posts':
            result = await getMyPosts(pageNum, size);
            const transformedPosts = transformPostData(result.content);
            
            if (isRefresh) {
              setData(transformedPosts);
            } else {
              setData(prev => [...prev, ...transformedPosts]);
            }
            
            setHasMore(!result.last);
            break;
            
          case 'comments':
            result = await getMyReplies(pageNum, size);
            const transformedComments = transformCommentData(result.content);
            
            if (isRefresh) {
              setData(transformedComments);
            } else {
              setData(prev => [...prev, ...transformedComments]);
            }
            
            setHasMore(!result.last);
            break;
            
          case 'commented':
            result = await getMyRepliedPosts(pageNum, size);
            const transformedRepliedPosts = transformPostData(result.content);
            
            if (isRefresh) {
              setData(transformedRepliedPosts);
            } else {
              setData(prev => [...prev, ...transformedRepliedPosts]);
            }
            
            setHasMore(!result.last);
            break;
        }
      }
    } catch (err) {
      console.error('데이터 로드 중 오류:', err);
      setError(err instanceof Error ? err.message : '데이터를 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchQuery]); // loading을 의존성에서 제거

  // 더 불러오기
  const loadMore = useCallback(() => {
    if (!isSearchMode && hasMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadData(nextPage);
    }
  }, [isSearchMode, hasMore, loading, page, loadData]);

  // 새로고침
  const refetch = useCallback(() => {
    setPage(0);
    setData([]);
    setHasMore(true);
    loadData(0, true);
  }, [loadData]);

  // 탭 변경 또는 검색어 변경 시 초기화 및 데이터 로드
  useEffect(() => {
    setPage(0);
    setData([]);
    setHasMore(true);
    loadData(0, true);
  }, [activeTab, searchQuery]); // loadData를 의존성에서 제거하고 대신 필요한 값들만 사용

  return {
    data,
    loading,
    error,
    hasMore,
    loadMore,
    refetch
  };
};
